/**
 * Support Page
 * Support cases management and tracking
 */

import React, { useState } from 'react';
import PageLayout from '@components/PageLayout';
import { useSupport } from '../context/SupportContext';
import { ticketApiClient } from '../services/ticketApiClient';
import styles from './SupportPage.module.css';

interface SupportCase {
  id: string;
  status: 'Needs attention' | 'In-progress' | 'Not-started' | 'Complete';
  description: string;
  caseOwner: string;
  lastUpdated: string;
}

const SupportPage: React.FC = () => {
  const { refreshMetrics } = useSupport();
  const [activeTab, setActiveTab] = useState<'my' | 'org'>('my');
  const [searchQuery, setSearchQuery] = useState('');
  const [supportCases, setSupportCases] = useState<SupportCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | boolean>(false);

  React.useEffect(() => {
    const fetchCases = async (scope: 'my' | 'org' = activeTab) => {
      try {
        setLoading(true);
        setError(false);

        // 1. Fetch tickets (This also returns the summary/metrics)
        const data = await ticketApiClient.getTickets(undefined, scope);
        console.log(`[SupportPage] API Response for scope ${scope}:`, data);

        if (data && data.tickets) {
          const mappedCases: SupportCase[] = data.tickets.map(t => ({
            id: t.ticketNumber,
            status: mapCrmStatus(t.status, t.severityCode),
            description: t.title,
            caseOwner: t.ownerName || 'Dynamics CRM',
            lastUpdated: formatLastUpdated(t.modifiedOn || t.createdOn)
          }));
          setSupportCases(mappedCases);

          // 2. Sync global metrics for the sidebar (instead of a full separate refreshMetrics call)
          if (data.summary) {
            // We use the data we just got to update the context
            refreshMetrics(scope, data.summary);
          }
        }
      } catch (err: any) {
        console.error('[SupportPage] Error loading live cases:', err);
        // Distinguish between CRM context missing (404) and generic API failure
        if (err.response?.status === 404) {
          setError('Account context not found in CRM. Please verify your profile.');
        } else {
          setError('Live Cases Unavailable.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, [activeTab]); // Removed refreshMetrics to avoid extra calls/loops

  const mapCrmStatus = (status: string, severity: number): any => {
    const s = status.trim();

    // 'Needs Attention' -- no. of cases in the customer's bucket.
    const needsAttentionLabels = [
      'Waiting for Deflection',
      'Waiting for Customer',
      'Customer prefers to work on at a later date',
      'To Be Archived',
      'Waiting for Customer - Patch Pending Deployment',
      'Waiting for Customer - Patch Pending 3rd Party',
      'Waiting for Jobs to Complete',
      'Waiting for MSP End User (Customer)'
    ];

    if (needsAttentionLabels.some(label => s.toLowerCase() === label.toLowerCase())) {
      return 'Needs attention';
    }

    // 'In Progress' -- no. of cases in Support's bucket.
    const inProgressLabels = [
      'Support to follow up',
      'Response Currently In Progress',
      'Support to Contact Customer',
      'Follow up for deflection result',
      'Callback Request',
      'Internal Escalation',
      'Metallic Infrastructure Patch Pending Deployment'
    ];

    if (inProgressLabels.some(label => s.toLowerCase() === label.toLowerCase())) {
      return 'In-progress';
    }

    // 'Not-Started' -- no. of new cases in status 'Support Response Needed'.
    if (s.toLowerCase() === 'support response needed') {
      return 'Not-started';
    }

    // Default fallbacks
    if (severity === 1) return 'Needs attention';
    if (s.toLowerCase().includes('closed') || s.toLowerCase().includes('resolved')) return 'Complete';

    return 'In-progress';
  };

  const formatLastUpdated = (date: string) => {
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 3600 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return d.toLocaleDateString();
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Needs attention':
        return styles.statusNeedsAttention;
      case 'In-progress':
        return styles.statusInProgress;
      case 'Not-started':
        return styles.statusNotStarted;
      case 'Complete':
        return styles.statusComplete;
      default:
        return '';
    }
  };

  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'Needs attention':
        return (
          <span className={styles.statusIcon} style={{ color: '#f97316' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L2 12l10 10 10-10L12 2z" />
            </svg>
          </span>
        );
      case 'In-progress':
        return (
          <span className={styles.statusIcon} style={{ color: '#3b82f6' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="10" />
            </svg>
          </span>
        );
      case 'Not-started':
        return (
          <span className={styles.statusIcon} style={{ color: '#ef4444' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="10" />
            </svg>
          </span>
        );
      case 'Complete':
        return (
          <span className={styles.statusIcon} style={{ color: '#22c55e' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
              <circle cx="12" cy="12" r="10" strokeWidth="2" />
            </svg>
          </span>
        );
      default:
        return null;
    }
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const totalPages = Math.ceil(supportCases.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, supportCases.length);
  const paginatedCases = supportCases.slice(startIndex, endIndex);

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  React.useEffect(() => {
    // Reset to page 1 when tab changes
    setCurrentPage(1);
  }, [activeTab]);

  return (
    <PageLayout>
      <div className={styles.unifiedContainer}>
        {/* Support Info */}
        <section className={styles.supportSection}>
          <h1 className={styles.title}>
            <span className={styles.supportIcon}>
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 18v-6a9 9 0 0 1 18 0v6"></path>
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"></path>
              </svg>
            </span>
            Support
          </h1>
          <p className={styles.subtitle}>
            Premium support and incident management for your cloud environment
          </p>
          <ul className={styles.introList}>
            <li>
              Commvault Support provides industry-leading assistance to help you maintain data availability, security, and resilience.
            </li>
            <li>
              Manage your support incidents, track resolution progress, and coordinate with Commvault engineers directly from this centralized dashboard.
            </li>
          </ul>
          <div className={styles.smallLine}></div>
        </section>

        <section className={styles.casesSection}>
          <div className={styles.casesHeader}>
            <h2 className={styles.casesTitle} style={{ fontSize: '1.8rem', fontWeight: 500 }}>
              {supportCases.length} cases
            </h2>
            <div className={styles.casesActions}>
              <button className={styles.searchButton}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </button>
              <button
                className={styles.addCaseButton}
                onClick={() => window.open('https://support.commvault.com/', '_blank')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Support Case
              </button>
            </div>
          </div>

          <div className={styles.tabsContainer}>
            <button
              className={`${styles.tab} ${activeTab === 'my' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('my')}
            >
              My Cases
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'org' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('org')}
            >
              My Organization's Cases
            </button>
          </div>

          <div className={styles.casesTable}>
            <div className={styles.tableHeader}>
              <div className={styles.columnCaseId}>Case ID</div>
              <div className={styles.columnStatus}>Status</div>
              <div className={styles.columnDescription}>Description</div>
              <div className={styles.columnOwner}>Case Owner</div>
              <div className={styles.columnUpdated}>Last Updated</div>
            </div>

            <div className={styles.tableBody}>
              {loading ? (
                <div style={{ padding: '2rem', textAlign: 'center', backgroundColor: '#111', color: '#888' }}>
                  Loading real-time cases...
                </div>
              ) : error ? (
                <div style={{ padding: '3rem', textAlign: 'center' }}>
                  <div style={{ color: '#ef4444', marginBottom: '1rem', fontSize: '1.1rem' }}>
                    {typeof error === 'string' ? error : 'Live Cases Unavailable.'}
                  </div>
                  <button
                    onClick={() => setActiveTab(t => t)} // Trigger re-effect
                    className={styles.addCaseButton}
                    style={{ background: '#333', borderColor: '#444' }}
                  >
                    🔄 Try Again
                  </button>
                </div>
              ) : supportCases.length === 0 ? (
                <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.5 }}>
                  No active support cases found in CRM.
                </div>
              ) : (
                paginatedCases.map((supportCase, index) => (
                  <div key={index} className={styles.tableRow}>
                    <div className={styles.columnCaseId}>{supportCase.id}</div>
                    <div className={styles.columnStatus}>
                      <span className={`${styles.statusBadge} ${getStatusClass(supportCase.status)}`}>
                        {getStatusIndicator(supportCase.status)} {supportCase.status}
                      </span>
                    </div>
                    <div className={styles.columnDescription}>{supportCase.description}</div>
                    <div className={styles.columnOwner}>{supportCase.caseOwner}</div>
                    <div className={styles.columnUpdated}>{supportCase.lastUpdated}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className={styles.pagination}>
            <div className={styles.paginationSection}>
              <span>Items per page:</span>
              <div className={styles.paginationDropdownContainer}>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className={styles.paginationSelect}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <svg className={styles.selectArrow} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9l6 6 6-6"></path>
                </svg>
              </div>
            </div>

            <div className={styles.paginationSection}>
              <span className={styles.paginationInfo}>
                {supportCases.length > 0 ? `${startIndex + 1} - ${endIndex}` : '0 - 0'} of {supportCases.length} items
              </span>

              <div className={styles.paginationGroup}>
                <div className={styles.paginationDropdownContainer}>
                  <select
                    value={currentPage}
                    onChange={(e) => setCurrentPage(Number(e.target.value))}
                    className={styles.paginationSelect}
                  >
                    {Array.from({ length: totalPages }, (_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                  <svg className={styles.selectArrow} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6"></path>
                  </svg>
                </div>

                <span>of {totalPages} pages</span>

                <button
                  className={styles.paginationButton}
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 18l-6-6 6-6"></path>
                  </svg>
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 18l6-6-6-6"></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    </PageLayout>
  );
};

export default SupportPage;
